(() => {
  const DESKTOP = "(min-width: 901px)";

  const setup = (root) => {
    const drawer = root.querySelector("[data-menu-drawer]");
    const toggle = root.querySelector("[data-menu-toggle]");
    if (!drawer || !toggle || drawer.dataset.drawerReady === "true") return;
    drawer.dataset.drawerReady = "true";

    const panel = drawer.querySelector(".menu-drawer__panel");
    const submenuToggles = [...panel.querySelectorAll("[data-submenu-toggle]")];
    let lastFocused = null;

    const isOpen = () => drawer.classList.contains("is-open");

    // Links in a collapsed submenu are inert, so the trap's first/last boundaries
    // stay in step with what the browser will actually focus.
    const focusables = () =>
      [...panel.querySelectorAll("a[href], button:not([disabled])")].filter(
        (el) => !el.closest("[inert]") && el.offsetParent !== null
      );

    const submenuOf = (button) =>
      panel.querySelector(`#${CSS.escape(button.getAttribute("aria-controls"))}`);

    const collapse = (button) => {
      const submenu = submenuOf(button);
      button.setAttribute("aria-expanded", "false");
      if (!submenu) return;
      submenu.style.maxHeight = "";
      submenu.inert = true;
    };

    const expand = (button) => {
      const submenu = submenuOf(button);
      button.setAttribute("aria-expanded", "true");
      if (!submenu) return;
      submenu.inert = false;
      submenu.style.maxHeight = `${submenu.scrollHeight}px`;
    };

    const open = () => {
      lastFocused = document.activeElement;
      drawer.classList.add("is-open");
      document.body.classList.add("menu-drawer-open");
      toggle.setAttribute("aria-expanded", "true");
      const closeButton = drawer.querySelector(".menu-drawer__close");
      if (closeButton) closeButton.focus();
    };

    const close = ({ restoreFocus = true } = {}) => {
      drawer.classList.remove("is-open");
      document.body.classList.remove("menu-drawer-open");
      toggle.setAttribute("aria-expanded", "false");
      submenuToggles.forEach(collapse);
      const target = lastFocused && lastFocused.isConnected ? lastFocused : toggle;
      lastFocused = null;
      if (restoreFocus) target.focus();
    };

    toggle.addEventListener("click", () => (isOpen() ? close() : open()));

    drawer.addEventListener("click", (event) => {
      if (event.target.closest("[data-menu-close]")) {
        close();
      } else if (event.target.closest("a[href]")) {
        // Same-page and hash links don't reload, which would otherwise leave the
        // drawer open over the anchor the visitor just asked for.
        close({ restoreFocus: false });
      }
    });

    submenuToggles.forEach((button) => {
      button.addEventListener("click", () => {
        if (button.getAttribute("aria-expanded") === "true") collapse(button);
        else expand(button);
      });
    });

    // Document-scoped so the Tab trap still fires if focus has drifted outside
    // the panel; the isConnected guard retires this listener's instance once the
    // theme editor swaps the section for a fresh render.
    document.addEventListener("keydown", (event) => {
      if (!drawer.isConnected || !isOpen()) return;

      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== "Tab") return;

      const items = focusables();
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!panel.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    });

    // Rotating to landscape can cross the breakpoint with the drawer still open,
    // which would leave the body scroll-locked behind the desktop nav.
    window.matchMedia(DESKTOP).addEventListener("change", (event) => {
      if (event.matches && isOpen()) close({ restoreFocus: false });
    });
  };

  const setupDesktopNav = (root) => {
    const nav = root.querySelector(".custom-nav");
    if (!nav || nav.dataset.navReady === "true") return;
    nav.dataset.navReady = "true";

    const disclosures = [...nav.querySelectorAll("[data-desktop-submenu]")];
    if (disclosures.length === 0) return;

    const closeOthers = (current) => {
      disclosures.forEach((details) => {
        if (details !== current) details.open = false;
      });
    };

    disclosures.forEach((details) => {
      const summary = details.querySelector("summary");

      details.addEventListener("toggle", () => {
        summary.setAttribute("aria-expanded", details.open ? "true" : "false");
        if (details.open) closeOthers(details);
      });

      // Closing on focusout keeps the open state and aria-expanded in step with
      // where keyboard focus actually is, instead of leaving a panel orphaned.
      details.addEventListener("focusout", (event) => {
        if (!details.contains(event.relatedTarget)) details.open = false;
      });
    });

    // Hover-open only where hover is the primary input; touch and keyboard get
    // the native summary toggle.
    if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
      disclosures.forEach((details) => {
        const item = details.closest(".custom-nav__item");
        let closeTimer = null;

        item.addEventListener("mouseenter", () => {
          clearTimeout(closeTimer);
          details.open = true;
        });

        // The grace period lets the pointer stray briefly (e.g. cutting a corner
        // off the panel) without snapping the menu shut.
        item.addEventListener("mouseleave", () => {
          clearTimeout(closeTimer);
          closeTimer = setTimeout(() => (details.open = false), 200);
        });
      });
    }

    // Document-scoped so clicks anywhere dismiss the menu; the isConnected guard
    // retires this instance once the theme editor swaps the section.
    document.addEventListener("click", (event) => {
      if (!nav.isConnected) return;
      disclosures.forEach((details) => {
        if (details.open && !details.contains(event.target)) details.open = false;
      });
    });

    document.addEventListener("keydown", (event) => {
      if (!nav.isConnected || event.key !== "Escape") return;
      const open = disclosures.find((details) => details.open);
      if (!open) return;
      open.open = false;
      open.querySelector("summary").focus();
    });
  };

  setup(document);
  setupDesktopNav(document);
  document.addEventListener("shopify:section:load", (event) => {
    setup(event.target);
    setupDesktopNav(event.target);
  });
})();
